import axios from "axios"
import { rootStore } from "@/store/root-store"
import { toastUtils } from "@/utils/toast-utils"

type StoredTokens = {
  access_token?: string
  token?: string
  accessToken?: string
}

const parseTokens = (raw: string | null): StoredTokens | string | null => {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed === "string") return parsed
    if (parsed && typeof parsed === "object") return parsed as StoredTokens
    return null
  } catch {
    // Handle legacy values that may have been stored without JSON encoding.
    return raw
  }
}

// Storage service for auth tokens (localStorage only).
const storageService = {
  getStorage: (): Storage | null => {
    if (typeof window === "undefined") return null
    return localStorage
  },

  getToken: () => {
    const storage = storageService.getStorage()
    if (!storage) return null
    return parseTokens(storage.getItem("auth_tokens"))
  },

  setToken: (tokens: string | StoredTokens) => {
    const storage = storageService.getStorage()
    if (!storage) return
    storage.setItem("auth_tokens", JSON.stringify(tokens))
  },

  clearToken: () => {
    const storage = storageService.getStorage()
    if (!storage) return
    storage.removeItem("auth_tokens")
  },
}

// Create axios instance
const axiosInstance = axios.create({
  // Use relative URL so that Next.js can proxy /api to the backend (avoids CORS)
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  },
  timeout: 30000, // 30 seconds
})

// Request interceptor for adding auth token
axiosInstance.interceptors.request.use(
  (config) => {
    let token: string | null = null
    
    // First try to get token from localStorage
    const storedToken = storageService.getToken()
    if (storedToken) {
      // Handle both string token and object with access_token property
      if (typeof storedToken === 'string') {
        token = storedToken
      } else if (typeof storedToken === 'object' && storedToken !== null) {
        const tokenObj = storedToken as { access_token?: string; token?: string; accessToken?: string }
        token = tokenObj?.access_token || tokenObj?.token || tokenObj?.accessToken || null
      }
    }
    
    // Fallback: try to get token from authStore if storage doesn't have it
    if (!token) {
      try {
        const authStore = rootStore.authStore
        if (authStore.tokens) {
          if (typeof authStore.tokens === 'string') {
            token = authStore.tokens
          } else if (typeof authStore.tokens === 'object' && authStore.tokens !== null) {
            const tokenObj = authStore.tokens as { access_token?: string; token?: string }
            token = tokenObj?.access_token || tokenObj?.token || null
          }
        }
      } catch {
        // AuthStore might not be available, ignore
      }
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Don't set Content-Type for FormData, let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"]
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor for handling token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If error is 401 Unauthorized and we haven't tried to refresh the token yet
    if (error.response?.status === 401) {
      // Only handle logout/redirect if we're not already on the login page
      // This prevents redirect loops
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        // Silent clear — avoid logout()'s "Logged Out" toast (interceptor shows Session Expired)
        rootStore.authStore.silentLogout()
        storageService.clearToken()
        toastUtils.error("Session Expired", "Please log in again")
        window.location.replace("/login")
      }
    }

    // Handle other errors
    if (error.response) {
      // Server responded with an error status
      // Error message can be accessed via error.response.data?.detail if needed
    } else if (error.request) {
      // Request was made but no response received
      toastUtils.error("Network Error", "No response received from server")
    } else {
      // Error in setting up the request
      toastUtils.error("Error", error.message)
    }

    return Promise.reject(error)
  },
)

export default axiosInstance
